import axios, { AxiosError as AxiosLibError, HttpStatusCode } from "axios";

import { logger } from "@/common/utils/logger";

import {
  ApiErrorResponse,
  ApiLogInfo,
  ApiStatus,
} from "@/api-services/types/api.types";
import { CustomErrorCodes } from "./custom-error-codes";

export class CustomApiException extends Error {
  errorCode: string;
  httpCode: HttpStatusCode;
  description: string;
  requestId: string;
  timestamp: string;
  context?: string;

  constructor(
    errorResponse: ApiErrorResponse,
    errorLogDetails: ApiLogInfo,
    context: string = "",
  ) {
    super(errorResponse?.error?.errorCode ?? errorResponse?.error?.error_code); // Pass the error code to the parent class
    this.errorCode =
      errorResponse?.error?.errorCode ?? errorResponse?.error?.error_code;
    this.httpCode = errorResponse?.error?.httpCode ?? 500;
    this.description =
      errorResponse?.error?.description || "An unknown error occurred";
    this.requestId = errorResponse?.requestId || "";
    this.timestamp = errorResponse?.timestamp || "";
    this.context = context;
    Object.setPrototypeOf(this, new.target.prototype);
    logger.apiError({
      ...errorLogDetails,
      responseTime: new Date().getTime() - errorLogDetails?.responseTime,
      errorCode:
        errorResponse?.error?.errorCode ?? errorResponse?.error?.error_code,
      httpCode: errorResponse?.error?.httpCode ?? 500,
      message: errorResponse?.error?.description || "An unknown error occurred",
    });
  }
}

export class AxiosError extends CustomApiException {
  constructor(
    requestId: string,
    timestamp: string,
    httpCode: HttpStatusCode,
    errorLogDetails: ApiLogInfo,
  ) {
    super(
      {
        statusCode: ApiStatus.FAILURE,
        requestId,
        timestamp,
        error: {
          httpCode,
          errorCode: CustomErrorCodes.AXIOS,
          description: "An error occured while calling the API",
        },
      },
      errorLogDetails,
    );
  }
}

export class NoDataApiException extends CustomApiException {
  constructor(
    requestId: string,
    timestamp: string,
    httpCode: HttpStatusCode,
    errorLogDetails: ApiLogInfo,
  ) {
    super(
      {
        statusCode: ApiStatus.FAILURE,
        requestId,
        timestamp,
        error: {
          httpCode,
          errorCode: CustomErrorCodes.NO_DATA,
          description: "No data was found in response",
        },
      },
      errorLogDetails,
    );
  }
}

export class InvalidDataApiException extends CustomApiException {
  constructor(
    requestId: string,
    timestamp: string,
    httpCode: HttpStatusCode,
    description: string,
    errorLogDetails: ApiLogInfo,
  ) {
    super(
      {
        statusCode: ApiStatus.FAILURE,
        requestId,
        timestamp,
        error: {
          httpCode,
          description,
          errorCode: CustomErrorCodes.INVALID_DATA,
        },
      },
      errorLogDetails,
    );
  }
}
export class NetworkApiException extends CustomApiException {
  constructor(
    requestId: string,
    timestamp: string,
    errorLogDetails?: ApiLogInfo,
  ) {
    super(
      {
        statusCode: ApiStatus.FAILURE,
        requestId,
        timestamp,
        error: {
          httpCode: HttpStatusCode.ServiceUnavailable,
          errorCode: CustomErrorCodes.AXIOS,
          description: "Network error occurred while calling the API",
        },
      },
      errorLogDetails,
    );
  }
}

export const isAxiosNetworkError = (error): error is AxiosLibError => {
  return axios.isAxiosError(error) && !error.response;
};
